import Icon from '@mdi/react';
import { mdiDownload, mdiFileDocumentOutline } from '@mdi/js';
import React, { useCallback, useMemo, useState } from 'react';
import { BaseDialog, Variables } from '../../base';
import { buildStatementPrintHtml, downloadStatementPdf } from './statement-print-download';
import type { StatementPrintViewModel } from './statement-print.types';
import * as S from './statement-print-preview.styles';

type StatementPrintPreviewProps = {
    model: StatementPrintViewModel;
    open?: boolean;
    onClose: () => void;
    onError?: (message: string) => void;
};

export const StatementPrintPreview: React.FC<StatementPrintPreviewProps> = ({
    model,
    open = true,
    onClose,
    onError,
}) => {
    const [frameHeight, setFrameHeight] = useState(1100);
    const [isDownloading, setIsDownloading] = useState(false);
    const html = useMemo(() => buildStatementPrintHtml(model), [model]);

    const handleFrameLoad = useCallback((event: React.SyntheticEvent<HTMLIFrameElement>): void => {
        const doc = event.currentTarget.contentDocument;
        const height = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight;
        if (height && height > 400) {
            setFrameHeight(height + 24);
        }
    }, []);

    const handleDownload = async (): Promise<void> => {
        setIsDownloading(true);
        try {
            await downloadStatementPdf(model);
        } catch (err) {
            console.error(err);
            onError?.('Unable to download PDF. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <BaseDialog
            open={open}
            fullScreen={true}
            expandable={true}
            onClose={onClose}
            aria-label="PDF preview — Patient Statement"
            slotProps={{
                paper: {
                    style: {
                        background: Variables.Colors.n15,
                    },
                },
            }}>
            <S.DialogBody>
                <S.Toolbar>
                    <Icon path={mdiFileDocumentOutline} size={0.85} color={Variables.Swatches.White} />
                    <S.ToolbarTitle>PDF preview — Patient Statement</S.ToolbarTitle>
                    <S.ToolbarActions>
                        <S.ToolbarGhostButton type="button" variant="unstyled" onClick={onClose}>
                            Close
                        </S.ToolbarGhostButton>
                        <S.ToolbarPrimaryButton
                            type="button"
                            variant="unstyled"
                            onClick={handleDownload}
                            disabled={isDownloading}>
                            <Icon path={mdiDownload} size={0.75} color={Variables.Swatches.White} />
                            {isDownloading ? 'Generating PDF...' : 'Download PDF'}
                        </S.ToolbarPrimaryButton>
                    </S.ToolbarActions>
                </S.Toolbar>
                <S.PaperWrap>
                    <S.PaperShadow>
                        <S.PreviewFrame
                            title="Patient statement preview"
                            srcDoc={html}
                            style={{ height: frameHeight }}
                            onLoad={handleFrameLoad}
                        />
                    </S.PaperShadow>
                </S.PaperWrap>
            </S.DialogBody>
        </BaseDialog>
    );
};
